import { ButtonStyledAsLink } from 'components/Button/styled';
import { StyledUploadFileForm } from './components/Styled';
import useUploadFormDOMEvents from './components/useUploadFormDOMEvents';
import { useState } from 'react';
import FileUploadModal from './components/FileUploadModal';
import ErrorModal from './components/ErrorModal';
import { alertErrorMessage } from 'utils/alerts';
import { isArray } from 'lodash';
import { icons } from 'assets/icons/index'
import { Horizontal } from 'gls';
import { style } from 'typestyle';


type UploadFileFormProps = {
  onUpload: (file: File) => Promise<any>;
  onDownloadTemplate: () => void;
  objectTitle: string;
  collapsible?: boolean
}

export default function UploadFileForm({
  onUpload,
  objectTitle,
  onDownloadTemplate,
  collapsible = false
}: UploadFileFormProps) {

  const { dropFileContainerRef, fileInputRef, fileIsDragging } =
    useUploadFormDOMEvents(handleFileSelect);
  const [confirmModal, setConfirmModal] = useState(false)
  const [failErrors, setFailErrors] = useState<string[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [collapsed, setCollapsed] = useState(false)

  function handleFileSelect(e: any) {
    // file handler to handle input select event and drop event to capture files.
    const isInputSelect = e.type === 'change';
    const file: any = isInputSelect ? e.target.files[0] : e.dataTransfer.files[0];

    setSelectedFile(file);
    setConfirmModal(true)
  }

  function selectFile() {
    fileInputRef.current?.click()
  }

  function handleDownload(e: React.SyntheticEvent) {
    e.preventDefault();
    onDownloadTemplate()
  }

  function handleUpload() {
    if (!selectedFile)
      return;

    setUploadLoading(true)
    onUpload(selectedFile)
      .then(() => onClose())
      .catch((err: any) => {
        setUploadLoading(false)
        setConfirmModal(false)

        if (isArray(err.detail) && err.detail.length === 1 && err.detail[0].message) {
          setFailErrors([err.detail[0].message])
        } else if (isArray(err.detail) && err.detail.length > 0 && "row_index" in err.detail[0]) {
          setFailErrors(err.detail.map((r: any) => `Error on line ${r.row_index}, ${r.column}: ${r.message}`));
        } else {
          alertErrorMessage('Error uploading file');
          onClose()
        }
      });
  }

  function onClose() {
    setConfirmModal(false)
    setUploadLoading(false)
  }

  return (
    <>
      <StyledUploadFileForm ref={dropFileContainerRef} className={fileIsDragging ? '--dragging' : ''}>
        <div className="form-inner">
          {!collapsed && (
            <>
              {collapsible &&
                <icons.ArrowDownIcon
                  tabIndex={0}
                  aria-label={collapsed ? "Expand" : "Collapse"}
                  className={collapseStyle(collapsed)}
                  onClick={() => setCollapsed(!collapsed)}
                  onKeyDown={e => e.key === "Enter" && setCollapsed(!collapsed)}
                />
              }
              <p>
                <label
                  role='link'
                  tabIndex={0}
                  className="label-button"
                  htmlFor="fileInput"
                  onClick={selectFile}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      selectFile()
                    }
                  }}
                >
                  Select
                </label>{' '}
                .xls, .xlsx or .csv file to upload or drag and drop here
              </p>
              <p>
                You can also&nbsp;
                <ButtonStyledAsLink onClick={handleDownload}> download the template</ButtonStyledAsLink> to
                ensure your file fits
                <br /> the format shown in the template
              </p>
              <input ref={fileInputRef} style={{ display: 'none' }} type="file" id="fileInput" />
            </>
          )}
          {collapsible && collapsed && (
            <Horizontal horizontalAlign='center' className={collapsedStyle} spacing={10}
              tabIndex={0}
              onClick={() => setCollapsed(!collapsed)}
              onKeyDown={e => e.key === "Enter" && setCollapsed(!collapsed)}>
              <span>Upload File</span>
              <icons.ArrowDownIcon aria-label='expand' className={collapseStyle(collapsed)} />
            </Horizontal>
          )}
        </div>
      </StyledUploadFileForm >

      {confirmModal && selectedFile &&
        <FileUploadModal
          objectTitle={objectTitle}
          selectedFile={selectedFile}
          loading={uploadLoading}
          onConfirm={handleUpload}
          onClose={onClose}
        />
      }

      {
        failErrors.length > 0 &&
        <ErrorModal
          objectTitle={objectTitle}
          errors={failErrors}
          onClose={() => setFailErrors([])}
        />
      }
    </>
  );
}


const collapseStyle = (close: boolean) => style({
  position: !close ? "absolute" : "relative",
  top: close ? 4 : 10,
  right: close ? 0 : 10,
  cursor: 'pointer',
  width: '14px',
  height: '14px',
  transform: `rotate(${close ? 0 : 180}deg)`
})

const collapsedStyle = style({
  cursor: "pointer",
  fontSize: '15px',
  fontWeight: '600'
})